import React, { useState, useMemo } from "react";
import { MdAddBox } from "react-icons/md";
import { IoIosRefreshCircle } from "react-icons/io";
import { useDispatch, useSelector } from "react-redux";
import {
  useReactTable,
  getCoreRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
} from "@tanstack/react-table";
import { SlOptionsVertical } from "react-icons/sl";
import ModalCreateGroups from "components/QuestionsAnswers/Answers/ModalCreateGroups";
import { getGroupOptionById, getGroupsOptionCompleteList } from "actions/Catalogs";
import { setGroupOptionById } from "redux/catalog.slice";
import { format, parseISO } from "date-fns";

const columnHelper = createColumnHelper();
const statusMap = {
  true: 'Activo',
  false: 'Inactivo',
};

const getColumns = (onViewDetails) => [
  columnHelper.accessor("id", { header: "Id Grupo" }),
  columnHelper.accessor("description", { header: "Nombre Grupo de Respuesta" }),
  columnHelper.accessor("noOptions", { header: "Respuestas Posibles" }),
  columnHelper.accessor("createdAt", {
    header: "Fecha Alta",
    cell: ({ getValue }) => {
      const value = getValue();
      if (value) return format(parseISO(value), "dd/MM/yyyy HH:mm");
      return value;
    },
  }),
  columnHelper.accessor("active", {
    header: "Estatus",
    cell: ({ getValue }) => statusMap[getValue()],
  }),
  columnHelper.display({
    id: "actions",
    cell: ({ row }) => (
      <div onClick={() => onViewDetails(row.original.id)} className="flex justify-center">
        <SlOptionsVertical className="rotate-180 text-xl cursor-pointer hover:text-yellow-800" />
      </div>
    ),
  }),
];

const Answers = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [filterText, setFilterText] = useState("");
  const { groupOptionList } = useSelector((state) => state.catalog);
  const dispatch = useDispatch();
  const { currentProject } = useSelector((state) => state.auth);

  //obtencion de grupo por id
  const onViewDetails = (groupId) => {
    if (groupId && groupId > 0) {
      dispatch(getGroupOptionById(groupId));
      setIsOpen(true);
    }
  }

  //Funcion para obtener el grupo de opciones completa
  React.useEffect(() => {
    if (currentProject && currentProject?.id) {
      dispatch(getGroupsOptionCompleteList());
    }
  }, [dispatch, currentProject]);

  const filteredQuestions = useMemo(() => {
    if (!filterText) return groupOptionList || [];
    return (groupOptionList || []).filter(q =>
      (q.name || q.description || "").toLowerCase().includes(filterText.toLowerCase())
    );
  }, [groupOptionList, filterText]);

  //creacion de tablas
  const table = useReactTable({
    data: filteredQuestions || [],
    columns: getColumns(onViewDetails),
    state: {
      pagination: {
        pageIndex,
        pageSize,
      },
    },
    onPaginationChange: (updater) => {
      const next = typeof updater === 'function' ? updater({ pageIndex, pageSize }) : updater;
      setPageIndex(next.pageIndex);
      setPageSize(next.pageSize);
    },
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  const onCreate = () => {
    dispatch(setGroupOptionById([]));
    setIsOpen(true);
  }

  const onRefresh = () => {
    dispatch(getGroupsOptionCompleteList());
  }

  return (
    <div className="flex flex-col w-full h-screen">
      <div className="bg-[#000000] rounded-t-lg h-[59px] w-full flex items-center justify-between px-4">
        <text className="text-[#ffffff] text-[16px] font-[Gravity] font-bold">
          Respuestas
        </text>
        <div className="flex gap-2 text-white text-4xl">
          <MdAddBox className="cursor-pointer" onClick={onCreate} />
          <IoIosRefreshCircle className="cursor-pointer" onClick={onRefresh} />
        </div>
      </div>
      <div className={`flex flex-row items-start w-full h-3/4 overflow-x-scroll overflow-y-scroll shadow-scroll-x`}>
        <div className="w-full bg-white px-6 pt-4 pb-6 rounded-b-xl shadow-xl">
          <div className="overflow-x-auto">
            <div className="w-full flex justify-start mt-4">
              <input
                type="text"
                placeholder="Buscar"
                value={filterText}
                onChange={e => setFilterText(e.target.value)}
                className="border border-gray-800 rounded px-3 py-2 w-72"
              />
            </div>
            <table className="min-w-full text-sm">
              <thead className="bg-white border-b-2 border-gray-400">
                {table.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th
                        key={header.id}
                        className="px-4 py-5 text-left font-semibold text-black whitespace-nowrap"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map(row => (
                  <tr key={row.id} className="border-b-2 border-gray-300 hover:bg-gray-50">
                    {row.getVisibleCells().map(cell => (
                      <td key={cell.id} className="px-4 py-2 whitespace-nowrap">
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* Paginación con estilo personalizado */}
          <div className="flex flex-row items-center justify-center mt-4">
            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_prev.svg`}
                width={50}
                alt="Anterior"
              />
            </button>

            {table.getCanPreviousPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() => table.setPageIndex(table.getState().pagination.pageIndex - 1)}
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex}
                </span>
              </button>
            )}

            <button
              className="bg-[#BCBEC1] m-2 rounded px-2 py-1 cursor-pointer"
              onClick={() => table.setPageIndex(table.getState().pagination.pageIndex)}
            >
              <span className="font-bold font-[Gravity] text-[12px]">
                {table.getState().pagination.pageIndex + 1}
              </span>
            </button>

            {table.getCanNextPage() && (
              <button
                className="m-2 px-2 py-1 cursor-pointer"
                onClick={() => table.setPageIndex(table.getState().pagination.pageIndex + 1)}
              >
                <span className="font-bold font-[Gravity] text-[12px]">
                  {table.getState().pagination.pageIndex + 2}
                </span>
              </button>
            )}

            <button
              className="w-[20px] h-[20px]"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <img
                src={`${process.env.PUBLIC_URL}/icons/btns/btn_arrow_next.svg`}
                width={50}
                alt="Siguiente"
              />
            </button>
          </div>
        </div>
      </div>
      <ModalCreateGroups isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </div>
  )
}

export default Answers